// ---------------------------------------------------------------------------
// Mercado Pago Point (posnet) — lógica pura del cobro presencial por terminal.
//
// Point NO usa la API de instore/QR: va por la Orders API
// (POST /v1/orders con type: 'point'). Este módulo concentra todo lo que se
// puede razonar sin red, para que sea testeable y para que las edge functions y
// el POS compartan exactamente las mismas reglas.
//
// Ver PLAN_MP_POINT_POS.md (raíz) para el diseño completo y las decisiones.
// ---------------------------------------------------------------------------

/** Expiración pedida a MP. Tiene que coincidir con POINT_TIMEOUT_MS: si el POS
 *  corta antes que MP, la terminal sigue pidiendo tarjeta con la venta ya cerrada. */
export const POINT_EXPIRATION = 'PT3M';

/** Timeout de espera del POS, en ms. Espejo de POINT_EXPIRATION. */
export const POINT_TIMEOUT_MS = 180000;

/** Estado de una venta cuyo cobro no se pudo cancelar con certeza. NO es ingreso:
 *  está declarado en CANCELLED_SALE_STATUSES (src/utils/flujoFondos.js). */
export const SALE_STATUS_REVISION = 'cobro_en_revision';

/** Estados de orden de la Orders API que MP puede devolver. */
export const POINT_ORDER_STATUSES = Object.freeze([
  'created', 'at_terminal', 'processed', 'canceled', 'expired', 'refunded',
]);

// ---------------------------------------------------------------------------
// Monto
// ---------------------------------------------------------------------------

/**
 * Monto para la Orders API: string decimal con 2 decimales ("24.00").
 * NO son centavos — mandar 2400 acá es cobrarle cien veces de más al cliente.
 * @param {number|string} total
 * @returns {string}
 */
export function formatPointAmount(total) {
  const value = Number(total);
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`Monto inválido para cobrar con Point: ${total}`);
  }
  // toFixed sobre un número normalizado: evita el ruido del float (0.1+0.2) y la
  // notación científica de los importes grandes.
  return (Math.round((value + Number.EPSILON) * 100) / 100).toFixed(2);
}

// ---------------------------------------------------------------------------
// Idempotencia
// ---------------------------------------------------------------------------

/** FNV-1a de 32 bits, determinístico y sin dependencias (corre en browser y Deno). */
function fnv1a(str, seed) {
  let hash = seed >>> 0;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

const hex8 = (n) => n.toString(16).padStart(8, '0');

/**
 * Idempotency-Key para POST /v1/orders, determinística por (venta, intento).
 *
 * - Mismo intento => misma key: un reintento por timeout de red hace que MP
 *   devuelva la orden que ya creó, en vez de mandar un segundo cobro a la terminal.
 * - Intento distinto => key distinta: después de un rechazo, el cajero puede
 *   reintentar y se crea una orden nueva (con la misma key se reusaría la
 *   rechazada y no cobraría nunca).
 *
 * @param {string} saleId  sales.id
 * @param {number} attempt intento (0-based); en la práctica, cuántos cobros ya
 *                         se registraron para esa venta en sale_gateway_payments.
 * @returns {string} UUID
 */
export function buildPointIdempotencyKey(saleId, attempt = 0) {
  const seedStr = `impulza-point:${String(saleId || '')}:${Number(attempt) || 0}`;
  const a = fnv1a(seedStr, 0x811c9dc5);
  const b = fnv1a(`${seedStr}:b`, a);
  const c = fnv1a(`${seedStr}:c`, b);
  const d = fnv1a(`${seedStr}:d`, c);
  const raw = `${hex8(a)}${hex8(b)}${hex8(c)}${hex8(d)}`;
  return [
    raw.slice(0, 8), raw.slice(8, 12), raw.slice(12, 16), raw.slice(16, 20), raw.slice(20, 32),
  ].join('-');
}

// ---------------------------------------------------------------------------
// Body de la orden
// ---------------------------------------------------------------------------

/**
 * Arma el body de POST /v1/orders para un cobro con terminal Point.
 *
 * installments_cost queda fijo en 'seller' a propósito: con 'buyer' Mercado Pago
 * le suma el costo de las cuotas al cliente EN LA TERMINAL, y el total de Impulza
 * (ticket, arqueo, factura AFIP) deja de coincidir con lo efectivamente cobrado.
 * El traslado del costo al cliente se hace con payment_methods.surcharge_percentage,
 * que ya se aplica al total de la venta y se imprime en el ticket.
 */
export function buildPointOrderBody({
  saleId,
  totalAmount,
  terminalId,
  method = {},
  ticketNumber,
  description,
} = {}) {
  if (!saleId) {
    throw new Error('Falta la referencia externa (sales.id) para crear la orden de Point.');
  }
  if (!terminalId) {
    throw new Error('Falta el ID de la terminal Point de la sucursal.');
  }

  const installments = Math.max(1, Number(method?.mp_default_installments) || 1);
  const defaultType = method?.mp_default_type === 'debit_card' ? 'debit_card' : 'credit_card';

  return {
    type: 'point',
    external_reference: String(saleId),
    expiration_time: POINT_EXPIRATION,
    description: description || `Venta ${String(saleId).slice(0, 8).toUpperCase()}`,
    transactions: {
      payments: [{ amount: formatPointAmount(totalAmount) }],
    },
    config: {
      point: {
        terminal_id: String(terminalId),
        print_on_terminal: method?.mp_print_on_terminal ? 'ticket' : 'no_ticket',
        ...(ticketNumber ? { ticket_number: String(ticketNumber) } : {}),
      },
      payment_method: {
        default_type: defaultType,
        default_installments: String(installments),
        // Fijo por diseño. No se toma de la config del método.
        installments_cost: 'seller',
      },
    },
  };
}

// ---------------------------------------------------------------------------
// Disponibilidad del método en el POS
// ---------------------------------------------------------------------------

/**
 * ¿Se puede ofrecer este método de cobro en la sucursal y el estado de red actual?
 *
 * Las pasarelas necesitan conexión (la terminal cobra online y el POS espera la
 * confirmación por Realtime) y necesitan su hardware configurado en la sucursal
 * seleccionada. Antes esto no se validaba: el cajero elegía el método, la venta
 * se procesaba a medias y recién ahí fallaba.
 *
 * @param {object} method  fila de payment_methods
 * @param {{sitio: object|null, isOnline: boolean}} ctx
 */
export function isPaymentMethodAvailable(method, { sitio, isOnline = true } = {}) {
  const gateway = String(method?.gateway_type || 'MANUAL').toUpperCase();

  // Efectivo / transferencia / cuenta corriente: siempre, también offline.
  if (gateway === 'MANUAL') return true;

  // De acá para abajo, todo necesita red.
  if (!isOnline) return false;

  if (gateway === 'GETNET') {
    return Boolean(method?.getnet_pos_id);
  }

  if (gateway === 'MERCADOPAGO') {
    // Sin sucursal seleccionada no se puede resolver el hardware.
    if (!sitio) return false;
    // mp_mode ausente = métodos creados antes de Point: se tratan como QR.
    const mode = String(method?.mp_mode || 'QR').toUpperCase();
    if (mode === 'POINT') return Boolean(sitio?.mp_terminal_id);
    return Boolean(sitio?.mp_pos_id);
  }

  return true;
}

// ---------------------------------------------------------------------------
// Lectura de la orden que devuelve MP
// ---------------------------------------------------------------------------

/** ¿La orden quedó efectivamente cobrada? Solo 'processed' cuenta. */
export function isPointApproved(order) {
  return String(order?.status || '').toLowerCase() === 'processed';
}

/**
 * Traduce una orden de la Orders API a la fila de sale_gateway_payments.
 * Guardar el tipo real (débito/crédito), las cuotas y la marca es lo que después
 * permite conciliar contra el resumen de MP y corregir la acreditación.
 */
export function mapOrderToGatewayPayment(order = {}) {
  const payment = order?.transactions?.payments?.[0] || null;
  const pm = payment?.payment_method || {};
  const amount = Number(payment?.amount ?? order?.total_amount);

  return {
    gateway_order_id: order?.id ? String(order.id) : null,
    gateway_payment_id: payment?.id ? String(payment.id) : null,
    status: order?.status ? String(order.status) : null,
    amount: Number.isFinite(amount) ? amount : null,
    payment_type: pm?.type ? String(pm.type) : null,
    card_brand: pm?.id ? String(pm.id) : null,
    card_last_four: pm?.card?.last_four_digits ? String(pm.card.last_four_digits) : null,
    installments: Number.isFinite(Number(pm?.installments)) && pm?.installments != null
      ? Number(pm.installments)
      : null,
    fee_amount: Number.isFinite(Number(payment?.fee_amount)) && payment?.fee_amount != null
      ? Number(payment.fee_amount)
      : null,
    raw: order,
  };
}
